import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Formik, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
// styles
import "react-datepicker/dist/react-datepicker.css";

// types
import { AssignmentType, GameItemTypes } from "src/redux/actions/assignments/types";

// components
import { SelectContentModal } from "./SelectContentModal";
import FilterModal from "src/modules/library/partials/filterModal";

// hooks
import { useFilterBooks } from "src/hooks/useFilterBooks";
import { useAppFlags } from "src/helpers/useAppFlags";

// utils
import { RootReducer } from "src/redux/reducers";
import {
  addContentToAssignmentActions,
  getBookGamesActions,
  unassignContentFromAssignmentActions
} from "src/redux/actions/assignments";

// assets
import DefaultBook from "src/assets/images/default-book.png";
import CrossIcon from "src/assets/images/crose-icon.svg";
import { groupBy } from "lodash";

/** ChallengeContentType: Book=1, QuizLevel1=3, QuizLevel2=4, QuizLevel3=5 */
const BOOK_CONTENT_TYPE = "1";
const QUIZ_LEVEL_CONTENT_TYPES = ["3", "4", "5"] as const;

const levelToContentType = (level: number) => String(level + 2);
const contentTypeToLevel = (contentType: string | number) => Number(contentType) - 2;

interface IProps {
  assignment: AssignmentType | undefined;
}

export const SecondStepSection: FC<IProps> = ({ assignment }) => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [t] = useTranslation();
  const { quizLmsEnabled } = useAppFlags();
  const [availableQuizzesByBook, setAvailableQuizzesByBook] = useState<Record<number, number[]>>({});

  const {
    book: { bookList, bookTypes, categoryList, cefrList, topicList }
  }: RootReducer = useSelector<RootReducer>((state) => state) as RootReducer;

  const {
    offset,
    searchPhrase,
    categoryIds,
    readingLevelValue,
    lexileLevelValue,
    resetSearch,
    handleSearch,
    handleSubmit: handleSubmitFilter,
    loadBookList,
    isLoadingBooksList,
    setOffset
  } = useFilterBooks();

  const handleUnassignContent = useCallback(
    (challengeContentId: number, contentType?: string | number) => {
      dispatch(
        unassignContentFromAssignmentActions.request({
          id: Number(id),
          challengeContentId,
          contentType
        })
      );
    },
    [dispatch, id]
  );

  const handleAddContentTypes = useCallback(
    (bookId: number, thumbnailUrl: string | undefined, contentTypes: string[]) => {
      if (!id || !contentTypes.length) return;
      dispatch(
        addContentToAssignmentActions.request({
          id: Number(id),
          content: [{ bookId, thumbnailUrl }],
          contentTypes
        })
      );
    },
    [dispatch, id]
  );

  const groupedContentData = useMemo(() => {
    const groupedData = groupBy(assignment?.content?.content, "bookId");

    return Object.keys(groupedData).map((bookId) => ({
      bookId: Number(bookId),
      contentTypes: groupedData[bookId].map((book) => String(book.contentType)),
      thumbnailUrl: groupedData[bookId][0].thumbnailUrl
    }));
  }, [assignment?.content?.content]);

  // Load which quiz levels exist for each assigned book (for pill availability).
  useEffect(() => {
    if (!quizLmsEnabled) return;
    const missing = groupedContentData
      .map(({ bookId }) => bookId)
      .filter((bookId) => availableQuizzesByBook[bookId] == null);
    if (!missing.length) return;

    // Mark in-flight so a re-render does not double-fetch.
    setAvailableQuizzesByBook((prev) => {
      const next = { ...prev };
      missing.forEach((bookId) => {
        if (next[bookId] == null) next[bookId] = [];
      });
      return next;
    });

    missing.forEach((bookId) => {
      dispatch(
        getBookGamesActions.request({
          id: bookId,
          onSuccess: (games: GameItemTypes[] | undefined) => {
            const levels = (games || [])
              .map((g) => g.lmsQuizLevel)
              .filter((level): level is number => level != null && level >= 1 && level <= 3);
            setAvailableQuizzesByBook((prev) => ({
              ...prev,
              [bookId]: Array.from(new Set(levels)).sort((a, b) => a - b)
            }));
          }
        })
      );
    });
  }, [quizLmsEnabled, groupedContentData, dispatch, availableQuizzesByBook]);

  useEffect(() => {
    setOffset(0);
    const isLoadBooks =
      !bookList?.books?.length && bookTypes?.length && categoryList?.length && cefrList?.length && topicList?.length;

    if (isLoadBooks) {
      loadBookList();
    }
  }, [bookTypes, categoryList, cefrList, topicList]);

  const toggleContentType = useCallback(
    (
      bookId: number,
      thumbnailUrl: string | undefined,
      contentType: string,
      selected: boolean,
      assignedTypes: string[]
    ) => {
      if (selected) {
        // Last activity for this book → drop the whole card.
        if (assignedTypes.filter((ct) => ct !== contentType).length === 0) {
          handleUnassignContent(bookId);
        } else {
          handleUnassignContent(bookId, contentType);
        }
      } else {
        handleAddContentTypes(bookId, thumbnailUrl, [contentType]);
      }
    },
    [handleAddContentTypes, handleUnassignContent]
  );

  const toggleAllQuizzes = useCallback(
    (bookId: number, thumbnailUrl: string | undefined, availableLevels: number[], assignedTypes: string[]) => {
      const quizTypes = availableLevels.map(levelToContentType);
      const allSelected = quizTypes.length > 0 && quizTypes.every((ct) => assignedTypes.includes(ct));
      if (allSelected) {
        quizTypes.forEach((ct) => handleUnassignContent(bookId, ct));
      } else {
        const toAdd = quizTypes.filter((ct) => !assignedTypes.includes(ct));
        handleAddContentTypes(bookId, thumbnailUrl, toAdd);
      }
    },
    [handleAddContentTypes, handleUnassignContent]
  );

  const quizLabel = (level: number) => t(`quiz_comprehension_level${level}_title`);

  return (
    <div className="ds-card sec_body">
      <Formik initialValues={{}} onSubmit={(e) => console.log(e)}>
        {() => {
          return (
            <>
              <div className="student-edit-form m-0 second-step">
                <div className="mt-5">
                  <h1>2. {t("assignment_create_second_step_title")}</h1>
                  <Form>
                    <div className="form-group">
                      <label className="label-text pt-2">{t("book_type_ebook")}</label>
                      <div className="row my-2">
                        {groupedContentData?.map(({ bookId, thumbnailUrl, contentTypes: assignedTypes }) => {
                          const availableLevels = availableQuizzesByBook[bookId] || [];
                          const assignedQuizTypes = assignedTypes.filter((ct) =>
                            (QUIZ_LEVEL_CONTENT_TYPES as readonly string[]).includes(ct)
                          );
                          const allQuizzesSelected =
                            availableLevels.length > 0 &&
                            availableLevels.every((level) => assignedTypes.includes(levelToContentType(level)));

                          if (!quizLmsEnabled) {
                            const labels = bookTypes?.filter(({ value }) =>
                              assignedTypes.find((type) => Number(type) == Number(value))
                            );
                            return (
                              <div className="col-md-3 col-sm-6 image-wrapper" key={bookId}>
                                <div className="thumb">
                                  <img src={thumbnailUrl || DefaultBook} alt="Img" className="img-fluid" />
                                </div>
                                <div className="types-wrapper">
                                  {labels?.map(({ value, name }) => (
                                    <div key={value} className="type-label">
                                      {t(name)}
                                    </div>
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  className="btn-close"
                                  aria-label="Delete book"
                                  onClick={() => handleUnassignContent(bookId)}
                                >
                                  <img src={CrossIcon} alt="" />
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div className="col-md-3 col-sm-6 image-wrapper" key={bookId}>
                              <div className="thumb">
                                <img src={thumbnailUrl || DefaultBook} alt="Img" className="img-fluid" />
                              </div>
                              <div className="chips-wrapper">
                                <button
                                  type="button"
                                  className={`chip${assignedTypes.includes(BOOK_CONTENT_TYPE) ? " selected" : ""}`}
                                  onClick={() =>
                                    toggleContentType(
                                      bookId,
                                      thumbnailUrl,
                                      BOOK_CONTENT_TYPE,
                                      assignedTypes.includes(BOOK_CONTENT_TYPE),
                                      assignedTypes
                                    )
                                  }
                                >
                                  {t("book_type_ebook")}
                                </button>
                                {availableLevels.map((level) => {
                                  const ct = levelToContentType(level);
                                  const selected = assignedTypes.includes(ct);
                                  return (
                                    <button
                                      type="button"
                                      key={ct}
                                      className={`chip${selected ? " selected" : ""}`}
                                      onClick={() =>
                                        toggleContentType(bookId, thumbnailUrl, ct, selected, assignedTypes)
                                      }
                                    >
                                      {quizLabel(level)}
                                    </button>
                                  );
                                })}
                                {/* Show pills for assigned quiz levels even if games fetch has not returned yet */}
                                {assignedQuizTypes
                                  .filter((ct) => !availableLevels.includes(contentTypeToLevel(ct)))
                                  .map((ct) => (
                                    <button
                                      type="button"
                                      key={ct}
                                      className="chip selected"
                                      onClick={() => toggleContentType(bookId, thumbnailUrl, ct, true, assignedTypes)}
                                    >
                                      {quizLabel(contentTypeToLevel(ct))}
                                    </button>
                                  ))}
                                {availableLevels.length > 1 && (
                                  <button
                                    type="button"
                                    className={`chip${allQuizzesSelected ? " selected" : ""}`}
                                    onClick={() =>
                                      toggleAllQuizzes(bookId, thumbnailUrl, availableLevels, assignedTypes)
                                    }
                                  >
                                    {t("All")}
                                  </button>
                                )}
                              </div>
                              <button
                                type="button"
                                className="btn-close"
                                aria-label="Delete book"
                                onClick={() => handleUnassignContent(bookId)}
                              >
                                <img src={CrossIcon} alt="" />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                      <button className="btn btn-edit" data-bs-toggle="modal" data-bs-target="#selectContentModal">
                        {t("school_campus_books_add_btn")}
                      </button>
                    </div>
                  </Form>
                </div>
              </div>
            </>
          );
        }}
      </Formik>
      {bookTypes && (
        <SelectContentModal
          offset={offset}
          bookList={bookList}
          isLoading={isLoadingBooksList}
          loadBookList={loadBookList}
          resetSearch={resetSearch}
          handleSearch={handleSearch}
          searchPhrase={searchPhrase}
          selectedIds={assignment?.content?.content.map(({ bookId, thumbnailUrl }) => ({ bookId, thumbnailUrl }))}
          bookTypes={bookTypes}
        />
      )}
      <FilterModal
        categoryId={categoryIds}
        readingLevelValue={readingLevelValue}
        lexileLevelValue={lexileLevelValue}
        modalIdToOpen="#selectContentModal"
        handleSubmit={handleSubmitFilter}
        setOffset={setOffset}
      />
    </div>
  );
};
